import { useState } from "react";
import StepNav, { CLINPATH_STEPS } from "@/components/StepNav";
import { CLINPATH_CASES, serveCase } from "@/data/clinPath";
import { useCaseList } from "@/context/ContentContext";
import { ClinPathIntro, ClinPathDebrief } from "@/components/clinpath/ClinPathParts";
import {
  HandlingBeat,
  QcBeat,
  HaemBeat,
  BiochemBeat,
  ActionBeat,
} from "@/components/clinpath/ClinPathBeats";

const pickRandom = (cases, excludeId) => {
  const pool = cases.filter((c) => c.id !== excludeId);
  const list = pool.length ? pool : cases;
  return list[Math.floor(Math.random() * list.length)];
};

export default function ClinPath() {
  const cases = useCaseList("clinpath", CLINPATH_CASES);
  const [stage, setStage] = useState("intro"); // intro | running | debrief
  const [caseData, setCaseData] = useState(null);
  const [beatIdx, setBeatIdx] = useState(0);
  const [scores, setScores] = useState([]);
  const [debrief, setDebrief] = useState(null);

  const beginCase = (excludeId) => {
    setCaseData(serveCase(pickRandom(cases, excludeId)));
    setBeatIdx(0);
    setScores([]);
    setDebrief(null);
    setStage("running");
  };

  const handleBeatDone = (scoreObj) => {
    const next = [...scores, scoreObj];
    if (beatIdx < 4) {
      setScores(next);
      setBeatIdx(beatIdx + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const earned = next.reduce((a, b) => a + b.earned, 0);
    const max = next.reduce((a, b) => a + b.max, 0) || 1;
    setDebrief({
      percent: Math.round((earned / max) * 100),
      breakdown: next,
      caseTitle: caseData.name,
    });
    setStage("debrief");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentStep = stage === "running" ? beatIdx + 1 : stage === "debrief" ? 5 : 1;
  const beatProps = { c: caseData, onDone: handleBeatDone };
  const beats = [
    <HandlingBeat key="handling" {...beatProps} />,
    <QcBeat key="qc" {...beatProps} />,
    <HaemBeat key="haem" {...beatProps} />,
    <BiochemBeat key="biochem" {...beatProps} />,
    <ActionBeat key="action" {...beatProps} />,
  ];

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <StepNav
        current={currentStep}
        steps={CLINPATH_STEPS}
        title="Pathology"
        subtitle="Cert IV clinical pathology trainer"
      />

      <main className="max-w-3xl mx-auto px-6 py-10">
        {stage === "intro" && (
          <ClinPathIntro caseCount={cases.length} onStart={() => beginCase(null)} />
        )}

        {stage === "running" && caseData && (
          <div data-testid="clinpath-running">
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#b45309] font-semibold">
                  Case · {caseData.name}
                </div>
                <div className="text-sm text-slate-600">
                  {caseData.breed} · {caseData.signalment}
                </div>
              </div>
              <div className="flex gap-1.5">
                {CLINPATH_STEPS.map((s) => (
                  <span
                    key={s.id}
                    className={`w-6 h-1.5 rounded-full ${s.id <= currentStep ? "bg-[#b45309]" : "bg-slate-200"}`}
                  />
                ))}
              </div>
            </div>
            {beats[beatIdx]}
          </div>
        )}

        {stage === "debrief" && debrief && (
          <ClinPathDebrief
            result={debrief}
            onNext={() => beginCase(caseData.id)}
            onRestart={() => setStage("intro")}
          />
        )}
      </main>
    </div>
  );
}
